import React, {
  useContext,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ChatDeclineList from '@rsos/components/capstone/Chat/ChatDeclineList';
import ChatHeader from '@rsos/components/capstone/Chat/ChatHeader';
import DataCard from '@rsos/components/capstone/DataCards/DataCardContainer';
import SplitPane from '@rsos/components/capstone/DataCards/SplitPane';
import {
  ADDRESS_PADDING,
  CHAT_MESSAGE_PADDING,
  DEFAULT_GRABBABLE_HEIGHT,
  DEFAULT_OFFSET,
} from '@rsos/components/capstone/DataCards/SplitPane/helpers';
import { Box } from '@rsos/components/capstone/base';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { NOTIFIED_AGENCY } from '@rsos/constants/alertsRoutingConstants';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import { QUEUE_FIXED_WIDTH } from '@rsos/constants/queueWidths';
import { removeAlert } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import useIsIRP from '@rsos/utils/useIsIRP';
import { getProductToken } from '../../utils/getProductToken';
import AlertChatBox from '../AlertChatBox';
import {
  CHAT_HEADER_HEIGHT,
  PRIVILEGED_CHAT_MARGIN,
} from '../AlertChatBox/AlertChatBox.styles';
import PrivilegedChat from '../PrivilegedChat';
import { StyledAlertPane, StyledChatPane } from './AlertPane.styles';
import { trackRemoveFromQueue } from './alertPaneTrackings';

const ALERTS_PANE_WIDTH = 360;
const ALERTS_PANE_PADDING = 30;
const DECLINE_PANE_WIDTH = 390;

/**
 * The alert details pane when an alert is selected.
 * @param {Function} onClose - Function that gets called when the pane is closed.
 * @param {function} onRemoveID - Function that updates the state to remove an alert from the map.
 */
const AlertPane = ({ onClose, onRemoveID }) => {
  const layout = useContext(LayoutContext).layout;
  const height = layout.body.height;
  const chatBoxRef = useRef(null);
  const privilegedChatRef = useRef(null);

  const dispatch = useDispatch();

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const { productToken = '' } = useSelector(state =>
    getProductToken(state.sinatra),
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const currentAlert = useSelectedAlert();

  const alertStatus = currentAlert?.status?.name;

  const privilegedConversation = useSelector(
    state => state.chat.chats[selectedAlertID]?.privileged_conversation,
  );
  const unreadPrivilegedAmount = privilegedConversation?.unreadMessages?.length;

  const authorizedEntity = currentAlert?.authorized_entity;

  const paneHeight = height - 30; // total height of the pane minus 15px padding on top & bottom

  const [bottomPaneHeight, setBottomPaneHeight] = useState('100%');

  const [isChatBoxVisible, setIsChatBoxVisible] = useState(
    privilegedConversation && alertStatus === ACCEPTED ? false : true,
  );

  const [isPrivilegedChatBoxVisible, setIsPrivilegedChatBoxVisible] = useState(
    privilegedConversation && alertStatus === ACCEPTED ? true : false,
  );

  const [showDeclinePane, setShowDeclinePane] = useState(false);

  const childHeight = paneHeight / 2;

  const { isIRPresponsiveConditional } = useIsIRP();

  const isAtBreakpoint = layout.window.width <= BP_LAPTOP;

  const windowInnerWidth = window.innerWidth;

  // Mimic functions in SplitPane, accounts for having 2 chats instead of just 1
  const availableHeight = height - DEFAULT_GRABBABLE_HEIGHT - DEFAULT_OFFSET;
  const minSize =
    availableHeight * 0.2 - ADDRESS_PADDING + CHAT_MESSAGE_PADDING;
  const minHeight = minSize + CHAT_HEADER_HEIGHT + PRIVILEGED_CHAT_MARGIN + 5;

  const paneWidth = useMemo(() => {
    return isAtBreakpoint && isIRPresponsiveConditional
      ? windowInnerWidth - ALERTS_PANE_PADDING - QUEUE_FIXED_WIDTH
      : isAtBreakpoint
      ? windowInnerWidth - ALERTS_PANE_PADDING
      : ALERTS_PANE_WIDTH;
  }, [isAtBreakpoint, isIRPresponsiveConditional, windowInnerWidth]);

  const declinePaneWidth = useMemo(() => {
    return isAtBreakpoint ? windowInnerWidth : DECLINE_PANE_WIDTH;
  }, [isAtBreakpoint, windowInnerWidth]);

  const toggleChatBoxVisibility = () => {
    if (isPrivilegedChatBoxVisible) {
      setIsPrivilegedChatBoxVisible(false);
    }

    setIsChatBoxVisible(prev => !prev);
  };

  const togglePrivilegedChatBoxVisibility = () => {
    if (isChatBoxVisible) {
      setIsChatBoxVisible(false);
    }

    setIsPrivilegedChatBoxVisible(prev => !prev);
  };

  const handleShowDeclinePane = isVisible => setShowDeclinePane(isVisible);

  const handleRemoveAlertFromQueue = () => {
    dispatch(removeAlert(selectedAlertID));
    onRemoveID(selectedAlertID);
    onClose();
    trackRemoveFromQueue(alertStatus, selectedAlertID, irpVersion);
  };

  /**
   * ALERTS-949 Adds a business requirement that if there are more than 1 chat
   * only 1 chat can be open at a time
   */
  const handleSplitPaneOnChange = () => {
    if (
      privilegedConversation &&
      !isPrivilegedChatBoxVisible &&
      !isChatBoxVisible
    )
      setIsPrivilegedChatBoxVisible(true);
    else if (!privilegedConversation && !isChatBoxVisible)
      setIsChatBoxVisible(true);
  };

  useEffect(() => {
    if (privilegedChatRef?.current) {
      const rect = privilegedChatRef.current.getBoundingClientRect();

      setBottomPaneHeight(
        `${rect.height + CHAT_HEADER_HEIGHT - PRIVILEGED_CHAT_MARGIN - 5}px`,
      );
    }
  }, [selectedAlertID, privilegedConversation]);

  const getMaxHeight = useCallback(() => {
    if (isChatBoxVisible || isPrivilegedChatBoxVisible) {
      return paneHeight;
    } else if (privilegedConversation) {
      return CHAT_HEADER_HEIGHT * 2 + PRIVILEGED_CHAT_MARGIN;
    } else {
      return CHAT_HEADER_HEIGHT;
    }
  }, [
    isChatBoxVisible,
    isPrivilegedChatBoxVisible,
    paneHeight,
    privilegedConversation,
  ]);

  useEffect(() => {
    if (privilegedConversation && alertStatus === ACCEPTED) {
      setIsChatBoxVisible(false);
      setIsPrivilegedChatBoxVisible(true);
    } else if (privilegedConversation) {
      setIsChatBoxVisible(true);
      setIsPrivilegedChatBoxVisible(false);
    } else if (!privilegedConversation) {
      setIsPrivilegedChatBoxVisible(false);
      setBottomPaneHeight('100%');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!privilegedConversation, selectedAlertID]);

  return currentAlert?.associated_role_type === NOTIFIED_AGENCY ? (
    <StyledAlertPane data-name="alert-event-pane">
      <Box width={paneWidth} height={paneHeight}>
        <DataCard
          accessToken={productToken}
          height={childHeight}
          onClose={onClose}
          isCentralStation={false}
          authorizedEntity={authorizedEntity}
        />
      </Box>
    </StyledAlertPane>
  ) : (
    <>
      <StyledAlertPane data-name="alert-event-pane">
        <SplitPane
          height={paneHeight}
          width={paneWidth}
          onChange={handleSplitPaneOnChange}
          onDragFinished={size => {
            setBottomPaneHeight(
              privilegedConversation
                ? `${size - PRIVILEGED_CHAT_MARGIN}px`
                : `${size}px`,
            );
          }}
          pane2Style={{
            maxHeight: getMaxHeight(),
            minHeight:
              privilegedConversation &&
              (isChatBoxVisible || isPrivilegedChatBoxVisible)
                ? minHeight
                : CHAT_HEADER_HEIGHT,
          }}
        >
          <DataCard
            accessToken={productToken}
            height={childHeight}
            onClose={onClose}
            isCentralStation={false}
            authorizedEntity={authorizedEntity}
          />
          <StyledChatPane>
            {privilegedConversation && (
              <PrivilegedChat
                chatRef={privilegedChatRef}
                isChatBoxVisible={isPrivilegedChatBoxVisible}
                unreadAmount={unreadPrivilegedAmount}
                privilegedConversation={privilegedConversation}
                onActionButtonClick={togglePrivilegedChatBoxVisibility}
                handleShowDeclinePane={handleShowDeclinePane}
                bottomPaneHeight={bottomPaneHeight}
                isUnread={currentAlert?.isUnread}
              />
            )}
            <Box
              height={privilegedConversation ? 'auto' : '100%'}
              grow={isChatBoxVisible}
              ref={chatBoxRef}
            >
              <ChatHeader
                alertChatParticipant={authorizedEntity}
                isChatBoxVisible={isChatBoxVisible}
                isUnread={currentAlert?.isUnread}
                onActionButtonClick={toggleChatBoxVisibility}
              />
              <AlertChatBox
                chatBoxHeight={bottomPaneHeight}
                isChatBoxVisible={isChatBoxVisible}
                onRemoveAlertFromQueue={handleRemoveAlertFromQueue}
                onShowDeclinePane={handleShowDeclinePane}
                hasPrivilegedChat={!!privilegedConversation}
              />
            </Box>
          </StyledChatPane>
        </SplitPane>
      </StyledAlertPane>
      {showDeclinePane && (
        <ChatDeclineList
          onRemoveAlertFromQueue={handleRemoveAlertFromQueue}
          onShowDeclinePane={handleShowDeclinePane}
          paneWidth={declinePaneWidth}
        />
      )}
    </>
  );
};

AlertPane.propTypes = {
  onClose: PropTypes.func.isRequired,
  onRemoveID: PropTypes.func.isRequired,
};

export default AlertPane;
